import { I18n } from '@affine/i18n';
import { AIStarIcon } from '@blocksuite/affine/components/icons';
import { getCommonBoundWithRotation } from '@blocksuite/affine/global/gfx';
import {
  ActionPlacement,
  type ToolbarModuleConfig,
} from '@blocksuite/affine/shared/services';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { SeniorToolExtension } from '@blocksuite/affine/widgets/edgeless-toolbar';
import { CommentIcon } from '@blocksuite/icons/lit';
import { html } from 'lit';

import {
  addAIChatBlock,
  getAIChatBlockBoundRightOf,
} from '../../_common/chat-actions-handle';
import { requestChatBlockInput } from '../../blocks/ai-chat-block/input-request';
import { AIProvider } from '../../provider';
import { openAIChatForHost } from '../../provider/ensure-provider';
import { runAIAction } from '../../provider/run-ai-action';
import type { EdgelessCopilotWidget } from '../../widgets/edgeless-copilot';
import { edgelessAIGroups } from './actions-config';

const ASK_AI_SELECTION_GAP_PX = 80;

const selectedAIChatSession = (models: readonly unknown[]) => {
  if (models.length !== 1) return null;
  const model = models[0] as {
    flavour?: string;
    props?: { rootWorkspaceId?: string; sessionId?: string };
  };
  if (model.flavour !== 'affine:embed-ai-chat' || !model.props?.sessionId) {
    return null;
  }
  return model;
};

export function setupEdgelessCopilot(widget: EdgelessCopilotWidget) {
  widget.groups = edgelessAIGroups;
}

export const aiChatBlockSeniorTool = SeniorToolExtension(
  'aiChatBlock',
  ({ block, toolbarContainer }) => ({
    name: I18n.t('com.streamlain.ai.chat.ask-ai'),
    content: html`<edgeless-ai-chat-tool-button
      .edgeless=${block}
      .toolbarContainer=${toolbarContainer}
    ></edgeless-ai-chat-tool-button>`,
    priority: 77,
  })
);

export function edgelessToolbarAIEntryConfig(): ToolbarModuleConfig {
  return {
    actions: [
      {
        placement: ActionPlacement.Start,
        id: 'A.open-ai-chat-in-sidebar',
        score: -2,
        when(ctx) {
          return !!selectedAIChatSession(ctx.getSurfaceModels());
        },
        content: ({ host }) => {
          const label = I18n.t('com.streamlain.ai.chat.canvas.open-in-sidebar');
          const openInSidebar = async () => {
            await openAIChatForHost(host);
            const gfx = host.std.get(GfxControllerIdentifier);
            const model = selectedAIChatSession(gfx.selection.selectedElements);
            const sessionId = model?.props?.sessionId;
            if (!sessionId) return;
            AIProvider.slots.requestOpenCanvasChatSession.next({
              workspaceId:
                model.props?.rootWorkspaceId || host.store.workspace.id,
              sessionId,
            });
          };
          return html`<edgeless-tool-icon-button
            aria-label=${label}
            data-testid="open-ai-chat-in-sidebar"
            @click=${() => runAIAction(host, openInSidebar)}
          >
            ${CommentIcon()}
            <span class="label medium">${label}</span>
          </edgeless-tool-icon-button>`;
        },
      },
      {
        placement: ActionPlacement.Start,
        id: 'A.ai-chat-block',
        score: -1,
        when(ctx) {
          const models = ctx.getSurfaceModels();
          return (
            !ctx.host.store.readonly$.value &&
            models.length > 0 &&
            !models.some(model => model.isLocked())
          );
        },
        content: ({ host }) => {
          const createChatBlock = () => {
            const gfx = host.std.get(GfxControllerIdentifier);
            const selectedElements = gfx.selection.selectedElements;
            if (!selectedElements.length) return;
            const selectedBound = getCommonBoundWithRotation(selectedElements);
            const chatBlockBound = getAIChatBlockBoundRightOf(
              selectedBound,
              ASK_AI_SELECTION_GAP_PX / Math.max(gfx.viewport.zoom, 0.01)
            );
            const placementCenter = {
              x: chatBlockBound.x + chatBlockBound.w / 2,
              y: chatBlockBound.y + chatBlockBound.h / 2,
            };
            const blockId = addAIChatBlock(
              host,
              [],
              '',
              placementCenter,
              gfx.layer.generateIndex()
            );
            if (!blockId) return;
            gfx.viewport.setViewport(
              gfx.viewport.zoom,
              [
                chatBlockBound.x + chatBlockBound.w / 2,
                chatBlockBound.y + chatBlockBound.h / 2,
              ],
              true
            );
            // Choosing the new chat block opens the chat in its lean form.
            requestChatBlockInput(blockId);
            gfx.selection.set({ elements: [blockId], editing: false });
          };
          return html`<edgeless-tool-icon-button
            aria-label=${I18n.t('com.streamlain.ai.chat.ask-ai')}
            data-testid="ask-ai-button"
            @click=${createChatBlock}
          >
            ${AIStarIcon}
            <span
              class="label medium"
              style="color: var(--streamlain-v2-brand-primary)"
              >${I18n.t('com.streamlain.ai.chat.ask-ai')}</span
            >
          </edgeless-tool-icon-button>`;
        },
      },
    ],
  };
}
